import type React from "react";
import { useEffect, useState } from "react";

import styled from "styled-components";

import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";

import { swSettings } from "app/common/services/swSettings";
import { isWebsiteCompareLocked, openLockedCompareHook } from "app/components/compare/WebsiteQueryBar/compareAccess";
import {
    hasCompletedWebsiteCompare,
    isRegisteredAfterCompareSuggestionRelease,
    markWebsiteCompareCompleted,
    shouldShowVisitsApplySelection,
    VISITS_OVER_TIME_APPLY_SELECTION_RELEASE_DATE,
} from "app/components/compare/WebsiteQueryBar/keyCompetitorsSuggestion";
import { translate } from "app/filters/i18nFilter";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { CHART_COLORS } from "../../../constants/ChartColors";
import type { ILegendProps } from "./legendsTypes";
import { GridBox, MainLegendContainer } from "./styledComponents";
import { Legends } from "./Legends";

const APPLY_SELECTION_KEY = "website_performance.features.visits_over_time.apply_selection_button";
const APPLY_SELECTION_AUTOMATION = "traffic-visits-over-time-apply-selection";

const AutoCompareWrapper = styled.div`
    position: relative;
    display: flex;
    flex-wrap: wrap;
    padding-left: 18px;
    border: 1px solid ${colorsPalettes.carbon[100]};
    border-radius: 6px;
    height: 68px;
`;

const Competitors = styled.div`
    padding: 8px 0;
`;

const VS = styled.div`
    background-color: ${colorsPalettes.carbon[0]};
    height: 12px;
    font-size: 11px;
    margin: 6px 3px 6px 6px;
`;

const Circle = styled.div`
    width: 24px;
    height: 24px;
    position: absolute;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 50%;
    left: -12px;
    top: 21px;
    line-height: normal;
    text-align: right;
    text-transform: uppercase;
    border: 1px solid ${colorsPalettes.carbon[100]};
    background: ${colorsPalettes.carbon[0]};
`;

const CompareButtonWrapper = styled.div`
    margin: auto;
    margin-right: 19px;
`;

const useCanApplyVisitsSelection = (selectedCompetitorCount: number): boolean => {
    const [isPreferencesReady, setIsPreferencesReady] = useState(false);
    const [hasCompletedCompare, setHasCompletedCompare] = useState(true);

    useEffect(() => {
        let isCancelled = false;
        void PreferencesService.whenReady().then(() => {
            if (isCancelled) {
                return;
            }
            setIsPreferencesReady(true);
            setHasCompletedCompare(hasCompletedWebsiteCompare());
        });
        return () => {
            isCancelled = true;
        };
    }, []);

    return shouldShowVisitsApplySelection({
        isPreferencesReady,
        hasCompletedCompare,
        isRegisteredAfterRelease: isRegisteredAfterCompareSuggestionRelease(
            swSettings.user?.createDate,
            VISITS_OVER_TIME_APPLY_SELECTION_RELEASE_DATE,
        ),
        selectedCompetitorCount,
    });
};

export const AutoCompareLegends: React.FunctionComponent<ILegendProps> = ({ legendItems, toggleSeries }) => {
    const colors = CHART_COLORS.compareMainColors;
    const mainSite = [];
    // extract the main site from the list
    mainSite.push(legendItems[0]);

    // extract the competitors from the list
    const competitors = legendItems.slice(1);
    competitors.map((item, index) => (item.color = colors[index + (1 % colors.length)]));

    const selectedCompetitors = competitors.filter((item) => item.visible ?? (item.hidden != null && !item.hidden));
    const canApplySelection = useCanApplyVisitsSelection(selectedCompetitors.length);

    const handleApplySelection = (): void => {
        const stateName = proNavigator.current().name;
        if (isWebsiteCompareLocked(swSettings)) {
            openLockedCompareHook("Locked visits over time apply selection", stateName);
            return;
        }

        const domains = [mainSite[0], ...selectedCompetitors].map((item) => item.name).join(",");
        allTrackers.dispatchTrackEvent("Compare", "click", `Visits over time apply selection/${stateName}/${domains}`);
        void proNavigator.updateParams({ key: domains });
        void markWebsiteCompareCompleted();
    };

    return (
        <GridBox>
            <MainLegendContainer>
                <Legends
                    legendItems={mainSite}
                    toggleSeries={toggleSeries}
                />
            </MainLegendContainer>
            <AutoCompareWrapper>
                <Circle>
                    <VS>VS.</VS>
                </Circle>
                <Competitors>
                    <Legends
                        legendItems={competitors}
                        toggleSeries={toggleSeries}
                    />
                </Competitors>
                {canApplySelection ? (
                    <CompareButtonWrapper>
                        <Button
                            type="outlined"
                            buttonHtmlType="button"
                            onClick={handleApplySelection}
                            dataAutomation={APPLY_SELECTION_AUTOMATION}
                        >
                            {translate(APPLY_SELECTION_KEY)}
                        </Button>
                    </CompareButtonWrapper>
                ) : null}
            </AutoCompareWrapper>
        </GridBox>
    );
};
